import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ExamLabelPicker, { type ExamLabel } from "./ExamLabelPicker";
import { MCQImageUpload } from "./MCQImageUpload";

interface MCQFormProps {
  topics: { id: string; name: string; parent_topic_id?: string | null }[];
  initial?: {
    topic_id?: string | null;
    question?: string;
    options?: string[];
    correct_index?: number;
    explanation?: string | null;
    difficulty?: number;
    tags?: string[] | null;
    stem_image_url?: string | null;
    explanation_image_url?: string | null;
  };
  submitting?: boolean;
  submitLabel?: string;
  onSubmit: (payload: {
    topic_id: string | null;
    question: string;
    options: string[];
    correct_index: number;
    explanation?: string | null;
    difficulty: number;
    tags: string[];
    stem_image_url: string | null;
    explanation_image_url: string | null;
  }) => void;
  onCancel?: () => void;
  examLabels?: ExamLabel[];
  examLabelIds?: string[];
  onLabelsChange?: (ids: string[]) => void;
}

const MCQForm = ({ topics, initial, submitting, submitLabel = "Save", onSubmit, onCancel, examLabels, examLabelIds = [], onLabelsChange }: MCQFormProps) => {
  const [topicId, setTopicId] = useState<string | "" | null>(initial?.topic_id ?? "");
  const [question, setQuestion] = useState(initial?.question ?? "");
  const [options, setOptions] = useState<string[]>(() => {
    const base = initial?.options ?? [];
    // Ensure 5 options
    return [0, 1, 2, 3, 4].map((i) => base[i] ?? "");
  });
  const [correctIndex, setCorrectIndex] = useState<number>(initial?.correct_index ?? 0);
  const [explanation, setExplanation] = useState<string>(initial?.explanation ?? "");
  const [difficulty, setDifficulty] = useState<number>(initial?.difficulty ?? 3);
  const [tags, setTags] = useState<string>(Array.isArray(initial?.tags) ? (initial!.tags || []).join(", ") : "");
  const [stemImageUrl, setStemImageUrl] = useState<string | null>(initial?.stem_image_url ?? null);
  const [explanationImageUrl, setExplanationImageUrl] = useState<string | null>(initial?.explanation_image_url ?? null);

  useEffect(() => {
    setTopicId(initial?.topic_id ?? "");
    setQuestion(initial?.question ?? "");
    const base = initial?.options ?? [];
    setOptions([0, 1, 2, 3, 4].map((i) => base[i] ?? ""));
    setCorrectIndex(initial?.correct_index ?? 0);
    setExplanation(initial?.explanation ?? "");
    setDifficulty(initial?.difficulty ?? 3);
    setTags(Array.isArray(initial?.tags) ? (initial!.tags || []).join(", ") : "");
    setStemImageUrl(initial?.stem_image_url ?? null);
    setExplanationImageUrl(initial?.explanation_image_url ?? null);
  }, [initial]);

  const setOption = (i: number, val: string) => setOptions((opts) => opts.map((o, idx) => (idx === i ? val : o)));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          topic_id: topicId ? String(topicId) : null,
          question: question.trim(),
          options: options.map((o) => o.trim()),
          correct_index: Number(correctIndex),
          explanation: explanation.trim() || null,
          difficulty: Number(difficulty) as number,
          tags: tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
          stem_image_url: stemImageUrl,
          explanation_image_url: explanationImageUrl,
        });
      }}
      className="space-y-3"
    >
      <div className="grid gap-2">
        <label className="text-sm">Topic</label>
        <select
          className="rounded-md border bg-background px-3 py-2"
          value={topicId ?? ""}
          onChange={(e) => setTopicId(e.target.value || "")}
        >
          <option value="">No topic</option>
          {topics.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-2">
        <label className="text-sm">Question</label>
        <Textarea value={question} onChange={(e) => setQuestion(e.target.value)} required />
      </div>

      <MCQImageUpload
        label="Question image (optional — ECG, scan, histology, etc.)"
        value={stemImageUrl}
        onChange={setStemImageUrl}
        pathPrefix="stems"
      />

      <div className="grid gap-2">
        <label className="text-sm">Options (A–E)</label>
        <div className="grid gap-2 sm:grid-cols-2">
          {options.map((opt, i) => (
            <Input key={i} value={opt} onChange={(e) => setOption(i, e.target.value)} placeholder={`Option ${i + 1}`} required />
          ))}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        <div className="grid gap-2">
          <label className="text-sm">Correct option</label>
          <Select value={String(correctIndex)} onValueChange={(v) => setCorrectIndex(Number(v))}>
            <SelectTrigger>
              <SelectValue placeholder="Select correct option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">A</SelectItem>
              <SelectItem value="1">B</SelectItem>
              <SelectItem value="2">C</SelectItem>
              <SelectItem value="3">D</SelectItem>
              <SelectItem value="4">E</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <label className="text-sm">Difficulty</label>
          <Select value={String(difficulty)} onValueChange={(v) => setDifficulty(Number(v))}>
            <SelectTrigger>
              <SelectValue placeholder="Difficulty" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 - Easy</SelectItem>
              <SelectItem value="2">2</SelectItem>
              <SelectItem value="3">3 - Medium</SelectItem>
              <SelectItem value="4">4</SelectItem>
              <SelectItem value="5">5 - Hard</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <label className="text-sm">Tags (comma separated)</label>
          <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. ECG, Cardiology" />
        </div>
      </div>

      <div className="grid gap-2">
        <label className="text-sm">Explanation</label>
        <Textarea value={explanation} onChange={(e) => setExplanation(e.target.value)} />
      </div>

      <MCQImageUpload
        label="Explanation image (optional)"
        value={explanationImageUrl}
        onChange={setExplanationImageUrl}
        pathPrefix="explanations"
      />

      {examLabels && onLabelsChange && (
        <ExamLabelPicker labels={examLabels} value={examLabelIds ?? []} onChange={onLabelsChange} />
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default MCQForm;
